import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { ApiClient, GovernanceJob } from "../api/client.js";

export function GovernancePage(props: {
  api: ApiClient;
  initialJobs?: readonly GovernanceJob[];
}): React.JSX.Element {
  const [jobs, setJobs] = useState<GovernanceJob[]>([...(props.initialJobs ?? [])]);
  const [customerId, setCustomerId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh(): Promise<void> {
    try {
      setJobs(await props.api.listGovernanceJobs());
      setError(null);
    } catch {
      setError("无法加载治理任务。");
    }
  }

  useEffect(() => { void refresh(); }, []);

  async function request(input: { type: "EXPORT_TENANT" } | { customerId: string; type: "DELETE_CUSTOMER" }): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const job = await props.api.requestGovernanceJob(input);
      setJobs((current) => [job, ...current.filter((item) => item.id !== job.id)]);
      if (input.type === "DELETE_CUSTOMER") setCustomerId("");
    } catch {
      setError("无法提交治理任务。");
    } finally {
      setBusy(false);
    }
  }

  async function requestDeletion(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    await request({ customerId: customerId.trim(), type: "DELETE_CUSTOMER" });
  }

  return <main>
    <h1>数据治理</h1>
    <p>导出与删除均为异步、可审计任务；删除请求不会由界面直接删除数据。</p>
    <section aria-label="租户数据导出">
      <h2>租户数据导出</h2>
      <button disabled={busy} onClick={() => { void request({ type: "EXPORT_TENANT" }); }} type="button">请求导出</button>
    </section>
    <section aria-label="客户数据删除">
      <h2>客户数据删除</h2>
      <form onSubmit={(event) => { void requestDeletion(event); }}>
        <label>客户 ID<input onChange={(event) => { setCustomerId(event.currentTarget.value); }} required value={customerId} /></label>
        <button disabled={busy || customerId.trim().length === 0} type="submit">请求删除客户数据</button>
      </form>
    </section>
    {error === null ? null : <p role="alert">{error}</p>}
    <section aria-label="治理任务列表">
      <h2>治理任务</h2>
      {jobs.length === 0 ? <p>暂无任务。</p> : <table>
        <thead><tr><th>类型</th><th>状态</th><th>创建时间</th><th>阻断原因</th></tr></thead>
        <tbody>{jobs.map((job) => <tr key={job.id}>
          <td>{job.type}{job.request.customerId === undefined ? "" : ` · ${job.request.customerId}`}</td>
          <td>{job.status}</td><td>{job.createdAt}</td><td>{job.blockedReason ?? "—"}</td>
        </tr>)}</tbody>
      </table>}
    </section>
  </main>;
}
